import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useZakeke } from '@zakeke/zakeke-configurator-react';
import { MOBILE_BREAKPOINT } from '../../Store';
import { Dialog, DialogWindow, useDialogManager } from '../dialogs/Dialogs';
import { Header } from './Header';
import { ImagesContainer } from './ImagesContainer';
import { Controls } from './Controls';
import { useAIProductStagingData } from './useAIProductStagingData';
import { useMobileFixedDropdopwnOpen, MobileDropdownProvider } from './FixedDropdown';
import { WebcamCaptureDialog } from './WebcamCaptureDialog';
import { ReactComponent as AiGenerateIcon } from '../../assets/icons/ai-generate.svg';

const StyledDialogWindow = styled(DialogWindow)`
	max-width: 100%;
	max-height: 100%;
	height: 100%;
	width: 100%;
	flex-basis: 100%;
	padding: 0;
	margin: 0;
	border-radius: 0;
`;

// Main entry point for AI Product Staging dialog
// This component orchestrates Header, Canvas, Controls, and OptionsPanel with full state management

interface AIProductStagingProps {
	onClose: () => void;
	initialMode?: 'product-staging' | 'virtual-tryon';
}

export const AIProductStaging: React.FC<AIProductStagingProps> = ({ onClose, initialMode }) => {
	const { T_static, isAiProductStagingEnabled, isAiVtoEnabled, getOnlineScreenshot } = useZakeke();
	const T = useMemo(() => ({ _: T_static }), [T_static]);
	const isDropdownOpen = useMobileFixedDropdopwnOpen();
	const { showDialog, closeDialog } = useDialogManager();

	// Capture product thumbnail on mount
	const [productThumbnailUrl, setProductThumbnailUrl] = useState<string | null>(null);
	const getOnlineScreenshotRef = useRef(getOnlineScreenshot);
	getOnlineScreenshotRef.current = getOnlineScreenshot;

	useEffect(() => {
		const captureThumbnail = async () => {
			try {
				const result = await getOnlineScreenshotRef.current(512, 512, false, undefined, 0);
				if (result?.originalUrl) {
					setProductThumbnailUrl(result.originalUrl);
				}
			} catch {
				// Thumbnail capture is optional
			}
		};
		captureThumbnail();
	}, []);

	// Load data asynchronously and get all state from hook
	// Track whether the input/3D view is expanded (toggled by thumbnail click)
	const [inputViewExpanded, setInputViewExpanded] = useState(false);

	const {
		mood,
		person,
		pose,
		background,
		surface,
		effect,
		isLoadingPreset,
		isLoadingGenerate,
		hasGenerated,
		setHasGenerated,
		activePanel,
		setActivePanel,
		modifiedImages,
		prompt,
		setPrompt,
		selectedBackground,
		selectedPerson,
		selectedPose,
		selectedSurface,
		selectedMood,
		selectedEffect,
		imageUrl,
		setImageUrl,
		inputImage,
		setInputImage,
		handleSelect,
		handleGenerate,
		handleUpload,
		handleDeleteVariant,
		hasUserUpload,
		handleWebcamCapture,
		modalType,
		setModalType,
		allowCustomBackground,
	} = useAIProductStagingData(initialMode);

	// Auto-switch to available mode if current mode has no rules
	useEffect(() => {
		if (modalType === 'product-staging' && !isAiProductStagingEnabled && isAiVtoEnabled) {
			setModalType('virtual-tryon');
		} else if (modalType === 'virtual-tryon' && !isAiVtoEnabled && isAiProductStagingEnabled) {
			setModalType('product-staging');
		}
	}, [modalType, isAiProductStagingEnabled, isAiVtoEnabled, setModalType]);

	// Handlers
	const handleWebcamOpen = useCallback(() => {
		showDialog(
			'webcam-capture',
			<WebcamCaptureDialog
				onCapture={(file) => {
					handleWebcamCapture(file);
					closeDialog('webcam-capture');
				}}
				onClose={() => closeDialog('webcam-capture')}
			/>
		);
	}, [showDialog, closeDialog, handleWebcamCapture]);

	const handleModeChange = useCallback(
		(mode: 'designer' | 'staging' | 'tryon') => {
			if (mode === 'designer') {
				// Close the dialog when switching to designer mode
				onClose();
			} else if (mode === 'staging') {
				setModalType('product-staging');
			} else if (mode === 'tryon') {
				setModalType('virtual-tryon');
			}
		},
		[onClose, setModalType]
	);

	const handleExpandInputView = useCallback(() => {
		setHasGenerated(false);
		setInputViewExpanded((v) => !v);
	}, [setHasGenerated]);

	const handlePanelToggle = useCallback(
		(panelId: string | null) => {
			if (activePanel === panelId) {
				setActivePanel(null);
			} else {
				setActivePanel(panelId);
			}
		},
		[activePanel, setActivePanel]
	);

	// Get selected option details for display
	const currentBg = background.presets.find((b) => b.id === selectedBackground);
	const currentSurface = surface.presets.find((s) => s.id === selectedSurface);
	const currentMood = mood.presets.find((m) => m.id === selectedMood);
	const currentEffect = effect.presets.find((e) => e.id === selectedEffect);
	const currentPerson = person.presets.find((p) => p.id === selectedPerson);
	const currentPose = pose.presets.find((p) => p.id === selectedPose);

	// Determine current mode for Header
	const currentMode = modalType === 'product-staging' ? 'staging' : 'tryon';

	return (
		<MobileDropdownProvider>
			<Dialog
				windowDecorator={StyledDialogWindow}
				showCloseButton={false}
				onClose={onClose}
				fullscreen
				maxHeight="calc(100vh)"
				overflow="auto"
				padding="0"
			>
				<ContentWrapper $hasActivePanel={activePanel !== null}>
				{/* tailwind: h-screen bg-gray-50 flex flex-col */}

				{/* Header with mode switching */}
				<Header
					currentMode={currentMode}
					onModeChange={handleModeChange}
					onClose={onClose}
					isLoadingGenerate={isLoadingGenerate}
					isInert={isDropdownOpen}
					isProductStagingEnabled={isAiProductStagingEnabled}
					isVtoEnabled={isAiVtoEnabled}
				/>

				{!hasGenerated && !isLoadingGenerate && !inputImage && !inputViewExpanded ? (
					<EmptyStateContainer {...(isDropdownOpen ? { inert: 'inert' } : {})}>
						<EmptyStateIcon>
							<AiGenerateIcon aria-hidden='true' />
						</EmptyStateIcon>
						<EmptyStateTitle>
							{modalType === 'virtual-tryon' ? T._('AI Try-On') : T._('AI Lifestyle Scenes')}
						</EmptyStateTitle>
						<EmptyStateDescription>
							{modalType === 'virtual-tryon'
								? T._('Try this product on yourself or a virtual model: choose options and/or enter a prompt to preview it here')
								: T._('Your product, your scene: customize options and/or type a prompt to generate and preview it here')}
						</EmptyStateDescription>
					</EmptyStateContainer>
				) : (
					<ImagesContainer
						hasGenerated={hasGenerated}
						variants={hasGenerated ? modifiedImages.map((url, id) => ({ id, url })) : []}
						onVariantClick={() => {}}
						selectedVariantId={undefined}
						isActive3D={!inputImage}
						isLoadingGenerate={isLoadingGenerate}
						isInert={isDropdownOpen}
						onUseAsInput={setInputImage}
						inputImage={inputImage}
						onClearInputImage={() => setInputImage(null)}
						onDeleteVariant={handleDeleteVariant}
					/>
				)}
				<ControlsAndOptionsWrapper>
					<Controls
						modalType={modalType}
						prompt={prompt}
						onPromptChange={setPrompt}
						activePanel={activePanel}
						onPanelToggle={handlePanelToggle}
						onGenerate={handleGenerate}
						isLoadingGenerate={isLoadingGenerate}
						selectedOptions={{
							background: currentBg,
							surface: currentSurface,
							mood: currentMood,
							effect: currentEffect,
							person: currentPerson,
							pose: currentPose,
						}}
						options={{
							background: background,
							surface: surface,
							mood: mood,
							effect: effect,
							person: person,
							pose: pose,
						}}
						isLoadingOptions={isLoadingPreset}
						selectedValues={{
							background: selectedBackground,
							surface: selectedSurface,
							mood: selectedMood,
							effect: selectedEffect,
							person: selectedPerson,
							pose: selectedPose,
						}}
						onSelect={handleSelect}
						imageUrl={imageUrl}
						setImageUrl={setImageUrl}
						handleUpload={handleUpload}
						hasUserUpload={hasUserUpload}
						allowCustomBackground={allowCustomBackground}
						inputImage={inputImage}
						onClearInputImage={() => setInputImage(null)}
						onWebcamOpen={handleWebcamOpen}
						inputThumbnailUrl={productThumbnailUrl}
						onExpandInputView={handleExpandInputView}
					/>
				</ControlsAndOptionsWrapper>
			</ContentWrapper>
		</Dialog>
		</MobileDropdownProvider>
	);
};

// Styled Components
// tailwind: h-screen bg-gray-50 grid grid-rows-[1fr_auto]
const ContentWrapper = styled.div<{ $hasActivePanel: boolean }>`
	background-color: #f9fafb;
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	position: relative;
	height: 100%;
	padding: 40px 60px;
	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		background-color: #f3f5f6;
		-webkit-overflow-scrolling: touch;
		padding: 0;
	}
`;

const ControlsAndOptionsWrapper = styled.div`
	display: flex;
	flex-direction: column;
`;

const EmptyStateContainer = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 2rem 1rem;
	animation: fadeIn 0.4s ease-out forwards;

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: scale(0.95);
		}
		to {
			opacity: 1;
			transform: scale(1);
		}
	}
`;

const EmptyStateIcon = styled.div`
	width: 64px;
	height: 64px;
	color: #c084fc;
	margin-bottom: 1rem;

	svg {
		width: 100%;
		height: 100%;
	}

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		width: 48px;
		height: 48px;
	}
`;

const EmptyStateTitle = styled.h2`
	font-size: 1.25rem;
	font-weight: 600;
	color: #374151;
	margin: 0 0 0.5rem 0;
	text-align: center;

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		font-size: 1.125rem;
	}
`;

const EmptyStateDescription = styled.p`
	font-size: 0.875rem;
	color: #9ca3af;
	text-align: center;
	max-width: 24rem;
	margin: 0;
	line-height: 1.5;
`;
